import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { api } from "../../utils/api";
import { API_ENDPOINTS } from "../../constants/api";
import ComponentErrorBoundary from "../ComponentErrorBoundary";
import MediaComponent from "../MediaComponent";

function LawyerRow({
  user,
  clients,
  currentUser,
  isExpanded,
  onToggleExpansion,
  onUserUpdate,
  onDeleteLawyer,
  onConfirmDelete,
  onCancelDelete,
  isDeletingUser,
  isConfirmingDelete,
  onEditRole,
  onUpdateRole,
  onCancelRoleEdit,
  onRoleChange,
  isEditingRole,
  isUpdatingRole,
  newRoleData,
}) {
  const [unassignedClients, setUnassignedClients] = useState([]);
  const [addingClient, setAddingClient] = useState(false);
  const [selectedClient, setSelectedClient] = useState("");
  const [assigningClient, setAssigningClient] = useState(false);
  const [removingClient, setRemovingClient] = useState(null);

  // Get role badge class
  const getRoleBadgeClass = (role) => {
    return `role-badge role-${role}`;
  };

  useEffect(() => {
    const assignedClientNames = user.assignedClientsList.map(
      (client) => client.name
    );

    setUnassignedClients(
      clients.filter(
        (client) => !assignedClientNames.includes(client.client_name)
      )
    );
  }, [user, clients]);

  // Handle adding client to lawyer
  const handleAddClient = () => {
    setAddingClient(true);
    setSelectedClient("");
  };

  // Handle canceling client addition
  const cancelAddClient = () => {
    setAddingClient(false);
    setSelectedClient("");
  };

  // Handle assigning client to lawyer
  const assignClientToLawyer = async () => {
    if (!selectedClient || assigningClient) return;

    setAssigningClient(true);
    try {
      // API call to assign client to lawyer
      const response = await api.post(API_ENDPOINTS.LAWYERS.ASSIGN_CLIENT, {
        lawyerId: user.id,
        clientName: selectedClient,
      });

      if (response.data.success) {
        // Update parent component with new user data
        const lawyer = response.data.lawyer;
        const transformedUser = {
          id: lawyer._id,
          name: lawyer.email.split("@")[0],
          email: lawyer.email,
          role: lawyer.role,
          roleLabel: lawyer.role_name,
          lastActive: "N/A", // Backend doesn't provide this, could be added later
          assignedClients: lawyer.assigned_clients.length,
          assignedClientsList: lawyer.assigned_clients, // Store the full client list
          createdAt: lawyer.created_at || "N/A",
          rawData: lawyer, // Store original data for expanded view
        };

        onUserUpdate(transformedUser);

        toast.success(`Client "${selectedClient}" assigned successfully`, {
          position: "top-right",
          autoClose: 3000,
        });

        // Reset state
        setAddingClient(false);
        setSelectedClient("");
      }
    } catch (err) {
      console.error("Failed to assign client:", err);
      toast.error("Failed to assign client. Please try again.", {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setAssigningClient(false);
    }
  };

  // Handle removing client from lawyer
  const removeClientFromLawyer = async (clientToRemove, clientIndex) => {
    const clientName =
      clientToRemove.name || clientToRemove.client_name || clientToRemove;

    if (removingClient === clientIndex) return;

    setRemovingClient(clientIndex);
    try {
      // API call to remove client from lawyer
      const response = await api.post(API_ENDPOINTS.LAWYERS.REMOVE_CLIENT, {
        lawyerId: user.id,
        clientName: clientName,
      });

      if (response.data.success) {
        // Update parent component with new user data
        const updatedUser = {
          ...user,
          assignedClients: user.assignedClients - 1,
          assignedClientsList: user.assignedClientsList.filter(
            (_, index) => index !== clientIndex
          ),
        };

        onUserUpdate(updatedUser);

        toast.success(`Client "${clientName}" removed successfully`, {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (err) {
      console.error("Failed to remove client:", err);
      toast.error("Failed to remove client. Please try again.", {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setRemovingClient(null);
    }
  };

  return (
    <React.Fragment>
      <tr className={isExpanded ? "expanded-row" : ""}>
        <td>{user.name}</td>
        <td>{user.email}</td>
        <td>
          <span className={getRoleBadgeClass(user.role)}>{user.roleLabel}</span>
        </td>
        <td>
          <span className="client-count">
            {user.assignedClients} client
            {user.assignedClients !== 1 ? "s" : ""}
          </span>
        </td>
        <td>
          <button
            className={`btn-small expand-btn ${isExpanded ? "expanded" : ""}`}
            onClick={() => onToggleExpansion(user.id)}>
            {isExpanded ? "Collapse" : "Expand"}
            <span className="expand-icon">{isExpanded ? "▲" : "▼"}</span>
          </button>
        </td>
        {currentUser?.role === 3 && (
          <td>
            {isEditingRole ? (
              <div className="role-edit-inline">
                <select
                  value={newRoleData.role}
                  onChange={onRoleChange}
                  disabled={isUpdatingRole}
                  className="role-select">
                  <option value={1}>Lawyer</option>
                  <option value={2}>Admin</option>
                  <option value={3}>Super Admin</option>
                </select>
                <div className="role-edit-buttons">
                  <button
                    className="role-save-btn"
                    onClick={() => onUpdateRole(user.id, user.email)}
                    disabled={isUpdatingRole}
                    title="Save role change">
                    {isUpdatingRole ? "⏳" : "✓"}
                  </button>
                  <button
                    className="role-cancel-btn"
                    onClick={onCancelRoleEdit}
                    disabled={isUpdatingRole}
                    title="Cancel role change">
                    ✕
                  </button>
                </div>
              </div>
            ) : (
              <button
                className="role-edit-btn"
                onClick={() => onEditRole(user.id, user.role)}
                disabled={isUpdatingRole}
                title="Edit user role">
                <span className="edit-icon">✏️</span>
              </button>
            )}
          </td>
        )}
        {currentUser?.role === 3 && (
          <td>
            {isConfirmingDelete ? (
              <div className="delete-confirmation-inline">
                <span className="confirm-text">Are you sure?</span>
                <div className="confirm-buttons">
                  <button
                    className="confirm-yes-btn"
                    onClick={() => onConfirmDelete(user.id, user.email)}
                    disabled={isDeletingUser}
                    title="Yes, delete lawyer">
                    {isDeletingUser ? "⏳" : "✓"}
                  </button>
                  <button
                    className="confirm-no-btn"
                    onClick={onCancelDelete}
                    disabled={isDeletingUser}
                    title="No, cancel deletion">
                    ✕
                  </button>
                </div>
              </div>
            ) : (
              <button
                className="delete-btn"
                onClick={() => onDeleteLawyer(user.id, user.email)}
                disabled={isDeletingUser}
                title="Delete lawyer">
                <span className="delete-icon">🗑️</span>
              </button>
            )}
          </td>
        )}
      </tr>
      {isExpanded && (
        <tr className="expanded-details">
          <td colSpan={currentUser?.role === 3 ? "7" : "5"}>
            <div className="lawyer-details">
              <div className="detail-section">
                <h4>Lawyer Information</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Full Email:</label>
                    <span>{user.email}</span>
                  </div>
                  <div className="detail-item">
                    <label>User ID:</label>
                    <span>{user.id}</span>
                  </div>
                  <div className="detail-item">
                    <label>Role Level:</label>
                    <span>{user.role}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <div className="clients-header">
                  <h4>Assigned Clients ({user.assignedClients})</h4>
                  {unassignedClients.length > 0 && (
                    <button
                      className="btn-small add-client-btn"
                      onClick={handleAddClient}
                      disabled={addingClient}>
                      Add Client
                    </button>
                  )}
                </div>

                {addingClient && (
                  <div className="add-client-form">
                    <div className="form-group">
                      <label htmlFor={`client-select-${user.id}`}>
                        Select Client to Assign:
                      </label>
                      <select
                        id={`client-select-${user.id}`}
                        value={selectedClient}
                        onChange={(e) => setSelectedClient(e.target.value)}
                        className="client-select">
                        <option value="">-- Select a client --</option>
                        {unassignedClients.map((client, index) => (
                          <option
                            key={index}
                            value={client.client_name}>
                            {client.client_name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="form-actions">
                      <button
                        className="btn-primary btn-small"
                        onClick={assignClientToLawyer}
                        disabled={!selectedClient || assigningClient}>
                        {assigningClient ? "Assigning..." : "Assign Client"}
                      </button>
                      <button
                        className="btn-secondary btn-small"
                        onClick={cancelAddClient}
                        disabled={assigningClient}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {user.assignedClientsList.length > 0 ? (
                  <div className="clients-list">
                    {user.assignedClientsList.map((client, index) => (
                      <div
                        key={index}
                        className="client-item-with-preview client-item-removable">
                        <div className="client-item-wrapper">
                          {client.preview && (
                            <div className="client-preview">
                              <ComponentErrorBoundary componentName="Media Preview">
                                <MediaComponent
                                  src={client.preview}
                                  alt={client.name || `Client ${index + 1}`}
                                  className="client-media"
                                  isPreview={true}
                                  fileType={
                                    client.preview.endsWith(".mp4")
                                      ? "video/mp4"
                                      : "image/png"
                                  }
                                />
                              </ComponentErrorBoundary>
                            </div>
                          )}
                          <div className="client-info">
                            <span className="client-name">
                              {client.name || client}
                            </span>
                            {client.description && (
                              <span className="client-description">
                                {client.description}
                              </span>
                            )}
                          </div>
                          <button
                            className={`remove-client-btn ${
                              removingClient === index ? "removing" : ""
                            }`}
                            onClick={() =>
                              removeClientFromLawyer(client, index)
                            }
                            disabled={removingClient === index}
                            title="Remove client from lawyer">
                            {removingClient === index ? (
                              <span className="removing-spinner">⏳</span>
                            ) : (
                              <span className="remove-icon">×</span>
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-clients">No clients assigned</p>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
}

export default LawyerRow;
